  <template>
    <div class="media-clipper-container">
      <loader v-if="!hasAssetData || isSaving" class="loader" />
      <div v-else class="player-main-container">
        <ConfirmModal
          v-if="modal.isOpen"
          :title="modal.title"
          :closeModal="closeModal"
          :assetData="modal.item"
          :actionType = "modal.actionType"
          :message = "modal.message"
          @action="handleModalAction"
        />
        <div class="player-container">
            <MediaPlayer ref="player" 
            :formatSecondsToTime="formatSecondsToTime"
            :source="assetData"
            :rewindOrFastForwardTime="rewindOrFastForwardTime"
            :fastForwardTime="fastForwardTime"
            @showTimelineOverlay="showTimelineOverlay"
            @timeupdate="updatePlaybackHandlePosition" 
            @setCurrentKeyframe = "setCurrentKeyframe"
            />
        </div>
        <div class="clip-timeline-container">
          <div class="clip-metadata">
            <MediaTimeline ref="timeline"
            @setPlayerActiveVideoTime ="setPlayerActiveVideoTime"  
            @requestVideoPlayer="requestVideoPlayer" 
            @reloadMediaPlayer="reloadMediaPlayer" 
            @setTimelineVideoTime="setTimelineVideoTime"
            @rewindVideoTime="rewindVideoTime"
            @fastForwardVideoTime="fastForwardVideoTime"
            @playMedia="playMedia"
            @pauseMedia="pauseMedia"
            @openConfirmationModal="openModal"
            @showPlayerLoader = "showPlayerLoader"
            @preloadKeyframesNearPlayhead = "preloadKeyframesNearPlayhead"
            @displayLoadedKeyframe = "displayLoadedKeyframe"
            @isTimelineHovering = "isTimelineHovering"
            :currentKeyFrameBlob = "currentKeyFrameBlob"
            :item = "assetData" 
            :formatSecondsToTime = "formatSecondsToTime" 
            :videoPlayer = "videoPlayer"
            />
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import { mapActions } from 'vuex';
  import MediaPlayer from '../components/media_player/media_player.vue';
  import MediaTimeline from '../components/media_player/media_timeline.vue';
  import Loader from '../components/loader';
  import ConfirmModal from '../components/media_player/confirmation_modal.vue';
  
  
  export default {
    data() {
      return {
        videoPlayer: null,
        mimeTypes: {
          mp4: "video/mp4", 
          mp3: "audio/mp3",
        },
        currentKeyFrameBlob: null,
        assetData: {},
        rewindOrFastForwardTime: 10,
        fastForwardTime: 10,
        modal: {
          isOpen: false,
          actionType: null,
          title: null,
          item: null
        },
        isSaving: false
      };
    },
    components: {
      MediaPlayer,
      Loader,
      MediaTimeline,
      ConfirmModal
    },
    computed:{
      hasAssetData() {
        return !!this.assetData && Object.keys(this.assetData).length > 0;
      },
      fileExtension() {
        if (!this.assetData?.mimeType) {
          return '';
        }
        const mimeType = this.assetData.mimeType;
        const extension = mimeType.split('/')[0];
        return extension.charAt(0).toUpperCase() + extension.slice(1);
      }
    },
    methods: {
      ...mapActions({
        fetchArticleOriginalExtendedAsset: "mediaClipper/fetchArticleOriginalExtendedAsset",
        ffprobeMediaDuration: "mediaClipper/ffprobeMediaDuration",
        updateArticle: "mediaClipper/updateArticle",
        uploadAssetToS3: "mediaClipper/uploadAssetToS3",
        fetchArticleAsset: "mediaClipper/fetchArticleAsset",
      }),

      async runMounted() {
        if (!this.$route.params.article_id) {
          window.alert(this.$t('mediaClipper.clippingMetadata.missing_param'));
        } else {
          this.fetchData(this.$route.params.article_id);
        }
      },
      
      async fetchData(articleId) {
        let originalAssetExtendedData = {};

        const articleAssetData = await this.fetchArticleAsset(articleId);
        const assetPath = articleAssetData.assetPath;
        const provider = articleAssetData.provider
        const airtime = articleAssetData.airtime


        const assetDurationAndType = await this.fetchAssetDurationAndType(assetPath);
        if (provider === 'carma_broadcast' || provider === 'carma_broadcast_curation'){
          originalAssetExtendedData = await this.fetchOriginalExtendedAssetData(articleId)
        }

        assetDurationAndType.articleId = this.$route.params.article_id;
        assetDurationAndType.airtime = airtime;
        assetDurationAndType.provider = provider;
        // Include deletedSegments from backend timelineData if available
        assetDurationAndType.deletedSegments = articleAssetData.timelineData?.deletedSegments || [];

        this.addMetaDataToPlayerAndTimeline(assetDurationAndType, originalAssetExtendedData);
      },

      // The archive already knows the recording's duration, format and path, so unlike the S3
      // asset below this one needs no ffprobe round-trip.
      async fetchOriginalExtendedAssetData(articleId){
        try{
          const originalExtendedAssetData = await this.fetchArticleOriginalExtendedAsset({articleId});
          if (originalExtendedAssetData.filepath) {
            originalExtendedAssetData.mergedFilePath = null;
            originalExtendedAssetData.mergedForwardAssetId = null;
            originalExtendedAssetData.mergedBackwardAssetId = null;
          }
          return originalExtendedAssetData;
        }catch(e){
          console.log(e)
          return null;
        }
      },

      async fetchAssetDurationAndType(path) {
        let mimeType;
        let duration;
        let data = await this.ffprobeMediaDuration({path, isLocal: false});
        if (data.mimeType ==='video') {
          mimeType = this.mimeTypes.mp4;
        } else if (data.mimeType ==='audio') {
          mimeType = this.mimeTypes.mp3;
        }
        duration = parseFloat(data.duration);
        if (!isNaN(duration)) {
          duration = parseFloat(duration.toFixed(1));
        }
        return {
          duration: duration,
          mimeType: mimeType,
          src: data.url,
          filepath: data.filepath
        }
      },

      async handleModalAction(actionType, itemMetadata){
        if (actionType === 'clip') {
          this.$refs.timeline.handleClipping(itemMetadata);
        } else if (actionType === 'delete') {
          this.$refs.timeline.handleDeleteSection(itemMetadata);
        }else if (actionType === 'save'){
          this.saveClipping();
        }
        this.closeModal();
      },

      async saveClipping() {
          this.isSaving = true;
          const editTimeline = this.buildEditTimeline();
          let data = {
            src: this.assetData.src,
            articleId: this.assetData.articleId,
            isLocal: this.assetData.isMerged || this.assetData.isExtendedToOriginal || this.assetData.isDeleted,
            airtime : this.assetData.airtimeToCompute,
            newAirTime: this.$refs.timeline.calculateAirtimeWithVideoOffset(0),
            clipLength: this.formatSecondsToTime(this.assetData.duration, true),
            editTimeline,
            lastAction: this.assetData.lastAction
          }
        try {
          const newAssetSrc = await this.uploadAssetToS3(data);
          data.s3Filename = newAssetSrc.s3Filename;
          data.thumbnailS3Filename = newAssetSrc.thumbnailS3Filename || null;
          if (newAssetSrc.url) {
            const updateArticleResponse = await this.updateArticle(data);
            if(!updateArticleResponse.assetId) throw new Error('Error while saving new Asset')
            this.closeModal();
            this.assetData = {};
            await this.runMounted();
            this.reloadOpenerWindow();
            this.isSaving = false;
          }else{
            window.alert(this.$t('mediaClipper.form.s3_upload_error'));
          }
        } catch (error) {
          this.isSaving = false;
          console.error(error);
          window.alert(this.$t('mediaClipper.form.save_error'));
        }
      },

      buildEditTimeline() {
        const timeline = this.$refs.timeline;
        if (!timeline || !this.assetData) {
          return null;
        }

        const sourceStartTime = timeline.currentToOriginalTime(this.assetData, 0);
        const sourceEndTime = timeline.currentToOriginalTime(this.assetData, this.assetData.duration);

        return {
          sourceStartTime,
          sourceEndTime,
          originalDuration: this.assetData.originalTimelineDuration || this.assetData.duration,
          baseAssetId: this.assetData.originalAssetExtendedData?.mergedBackwardAssetId ||
            this.assetData.originalAssetExtendedData?.currentAssetId || null,
          enteredExtendedMode: Boolean(this.assetData.isExtendedToOriginal),
          usedMerge: Boolean(this.assetData.isMerged),
          deletedSegments: this.assetData.deletedSegments || []
        };
      },

      reloadOpenerWindow() {
        try {
          if (window.opener) {
            window.opener.location.reload();
          }
        } catch (error) {
          console.warn('Unable to reload opener window after save.', error);
        }
      },

      closeModal(){
        this.modal.isOpen = false;
      },

      openModal(actionType, item) {
        this.modal.title = actionType.charAt(0).toUpperCase() + actionType.slice(1) + ' ' + this.fileExtension;
        this.modal.message = `Are you sure you want to ${actionType}?<br>`;
        this.modal.item = item;
        this.modal.actionType = actionType;
        if (actionType === 'clip') {
          this.modal.message += `New Duration: ${this.formatSecondsToTime(item.handles.end.time - item.handles.start.time)}`;
        } else if (actionType === 'delete') {
          this.modal.message += `New Duration: ${this.formatSecondsToTime(item.duration - (item.handles.end.time - item.handles.start.time))}`;
        }else if (actionType === 'save'){
          this.modal.message += `New Duration: ${this.formatSecondsToTime(item.duration)}`;
        }
        this.modal.isOpen = true;
      },

      addMetaDataToPlayerAndTimeline(data, originalAssetExtendedData = {}) {
        const metadata = {
          articleId: data.articleId,
          provider: data.provider,
          airtime: data.airtime,
          src: data.src,
          articleAirTime: data.airtime,
          mimeType: data.mimeType,
          duration: Math.floor(data.duration),
          keyframes: [],
          handleLeft: 0,
          currentPlaybackTime: 0,
          handles: {
            start: { position: 5, time: (5/100) * data.duration },
            end: { position: 50, time: (50/100) * data.duration }
          },
          clippedStartOffset: 0,
          isPlaying: false,
          isDeleted: false,
          isClipped: false,
          clippedAssetObj: {filepath: null},
          deletedAssetObj: {filepath: null},
          // props below are specific to carma_broadcast provider
          originalAssetUnExtendedData: {duration: data.duration, src: data.src, keyframes: null, mimeType: data.mimeType }, 
          originalAssetExtendedData,
          isExtendedToOriginal: false,
          isMerged: false,
          airtimeToCompute: null,
          // Piecewise airtime mapping for delete operations
          deletedSegments: data.deletedSegments || [], // Array of {startTime, endTime} in seconds from backend
          originalTimelineDuration: data.duration, // Reference timeline duration
        };
        this.assetData = metadata;
      },

      requestVideoPlayer() {
        if (this.$refs.player && this.$refs.player.player) {
          this.videoPlayer = this.$refs.player.player;
        } else {
          console.error("Video player not initialized or not found.");
        }
      },

      reloadMediaPlayer(showOverlay){
        this.$refs.player.load(showOverlay);
      },

      playMedia(time){
        this.assetData.isPlaying = true;
        this.$refs.player.handleMediaPlay(time);
      },

      pauseMedia(time){
        this.assetData.isPlaying = false;
        this.$refs.player.handleMediaPause(time);
      },

      rewindVideoTime(){
        this.$refs.player.rewindVideo(this.rewindOrFastForwardTime);
      },

      fastForwardVideoTime(){
        this.$refs.player.fastForwardVideo(this.rewindOrFastForwardTime);
      },

      updatePlaybackHandlePosition(playbackTime, newTime = null) {
        this.$refs.timeline.updatePlaybackHandlePosition(this.assetData, playbackTime, newTime);
      },

      setTimelineVideoTime(videoTime) {
        this.$refs.player.setCurrentTime(videoTime);
      },

      setPlayerActiveVideoTime(videoTime) {
          this.$refs.player.setCurrentTime(videoTime);
      },

      showPlayerLoader(){
        this.$refs.player.showLoader()
      },

      setCurrentKeyframe(url){
        this.currentKeyFrameBlob = url;
      },

      isTimelineHovering(state){
        this.$refs.player.setIsHovering(state);
      },

      preloadKeyframesNearPlayhead(time){
        this.$refs.player.preloadKeyframesNearPlayhead(time, 30)
      },

      displayLoadedKeyframe(time){
        this.$refs.player.displayLoadedKeyframe(time)
      },

      showTimelineOverlay(state){
        this.$refs.timeline.showOverlay(state);
      },

      formatSecondsToTime(seconds, HHMMSS = false) {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = Math.floor(seconds % 60);
        if (HHMMSS || hours > 0) {
          return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
        }
        return `${minutes}:${secs.toString().padStart(2, '0')}`;
      },
    },

    async mounted() {
      await this.runMounted()
    }
  };
  </script>

<style lang="scss">

  .app-media_clipper{
    .app-nav-toggle{
      display: none !important;
    }
  }

  .media-clipper-container {
    width: 100%;
    height: 100%;

    .modal-container {
      width: 300px !important;
      background-color: #202020 !important;
      color: white !important;
    }

    .loader {
      position: absolute !important;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 100;
    }

    .player-main-container {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      user-select: none;
      width: 85%;
      height: 80%;
      min-width: 748px;
      display: flex;
      flex-direction: column;
      box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2), 0 12px 48px rgba(0, 0, 0, 0.3);

      .player-container {
        width: 100%;
        min-height: 450px;
        height: 100%;
        background: black;
        position: relative;
        display: flex;
        justify-content: center;
        align-items: center;
      }

      .clip-timeline-container {
        position: relative;
        background-color: #111111;
        z-index: 10;
      }

      .max-content-width {
        width: max-content !important;
      }
    }
  }
</style>
  
